import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { usePageSEO } from "@/hooks/usePageSEO";
import {
  ArrowLeft, ArrowRight, Building2, FileText, Shield, CheckCircle,
  Loader2, MapPin, Phone, Mail, Users, Briefcase, Star, User, CreditCard
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ENTITY_TYPES = [
  { value: "LLC", label: "Limited Liability Company (LLC)", desc: "Most popular for small businesses. Flexible, simple, personal asset protection.", icon: Shield, popular: true },
  { value: "Corporation", label: "C-Corporation", desc: "Best for businesses seeking investors or planning to go public.", icon: Building2 },
  { value: "ProfessionalCorp", label: "Professional Corporation", desc: "For licensed professionals: doctors, lawyers, accountants, engineers.", icon: Briefcase },
  { value: "Nonprofit", label: "Nonprofit Corporation", desc: "For charitable, educational, religious, or scientific organizations.", icon: Users },
];

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky",
  "Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi",
  "Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico",
  "New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont",
  "Virginia","Washington","West Virginia","Wisconsin","Wyoming","District of Columbia"
];

const FILING_METHODS = [
  { value: "standard", label: "Standard Processing" },
  { value: "rush", label: "Rush Processing" },
  { value: "24hour", label: "24-Hour Processing (if available)" },
];

const PersonFields = ({ prefix, label, form, setForm, showSSN, showOwnership }) => (
  <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200 space-y-3">
    <p className="text-sm font-semibold text-neutral-900">{label}</p>
    <div className="grid grid-cols-2 gap-3">
      <div><Label className="text-xs">Name *</Label><Input value={form[`${prefix}Name`] || ""} onChange={e => setForm({ ...form, [`${prefix}Name`]: e.target.value })} placeholder="Full name" /></div>
      <div><Label className="text-xs">Address</Label><Input value={form[`${prefix}Address`] || ""} onChange={e => setForm({ ...form, [`${prefix}Address`]: e.target.value })} placeholder="Street address" /></div>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <div><Label className="text-xs">City</Label><Input value={form[`${prefix}City`] || ""} onChange={e => setForm({ ...form, [`${prefix}City`]: e.target.value })} placeholder="City" /></div>
      <div><Label className="text-xs">State</Label><Input value={form[`${prefix}State`] || ""} onChange={e => setForm({ ...form, [`${prefix}State`]: e.target.value })} placeholder="ST" /></div>
      <div><Label className="text-xs">ZIP</Label><Input value={form[`${prefix}Zip`] || ""} onChange={e => setForm({ ...form, [`${prefix}Zip`]: e.target.value })} placeholder="ZIP" /></div>
    </div>
    {showOwnership && (
      <div><Label className="text-xs">Percentage of Ownership</Label><Input value={form[`${prefix}Ownership`] || ""} onChange={e => setForm({ ...form, [`${prefix}Ownership`]: e.target.value })} placeholder="e.g., 50%" /></div>
    )}
    {showSSN && (
      <div><Label className="text-xs">Social Security # (S-Corp only)</Label><Input value={form[`${prefix}SSN`] || ""} onChange={e => setForm({ ...form, [`${prefix}SSN`]: e.target.value })} placeholder="XXX-XX-XXXX" type="password" /></div>
    )}
  </div>
);

export const BusinessFormation = () => {
  const [step, setStep] = useState(1);
  const [entityType, setEntityType] = useState("");
  const [filingState, setFilingState] = useState("");
  const [filingMethod, setFilingMethod] = useState("standard");
  const [sCorpElection, setSCorpElection] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [form, setForm] = useState({});
  const [useCorpnetRA, setUseCorpnetRA] = useState(true);
  const [isManagerManaged, setIsManagerManaged] = useState(false);
  const [showMember2, setShowMember2] = useState(false);
  const [showShareholder2, setShowShareholder2] = useState(false);
  const [showDirector2, setShowDirector2] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [orderMode, setOrderMode] = useState("formation"); // "formation" or "standalone"

  const toggleAddon = (service) => {
    setSelectedAddons(prev => prev.includes(service) ? prev.filter(s => s !== service) : [...prev, service]);
  };

  usePageSEO(
    "Business Formation Services | LLC, Corporation, Nonprofit | My Biz Consulting",
    "Form your LLC, Corporation, Professional Corp, or Nonprofit in any U.S. state. Professional filing services through My Biz Consulting."
  );

  const totalSteps = 4;

  const handleSubmitOrder = async () => {
    if (!form.companyName || !form.contactFirstName || !form.contactLastName || !form.contactEmail) {
      toast.error("Please fill in all required fields (company name, name, email)");
      return;
    }
    setSubmitting(true);
    try {
      const orderBody = {
        orderMode,
        entityType: orderMode === "formation" ? entityType : "Standalone Services",
        state: filingState, filingMethod, sCorpElection,
        selectedPackage: selectedPackage || "",
        selectedAddons,
        companyInfo: {
          companyName: form.companyName, alternativeName: form.altName || "",
          businessDescription: form.businessDescription || "",
          businessAddress1: form.bizAddress1 || "", businessAddress2: form.bizAddress2 || "",
          businessCity: form.bizCity || "", businessState: form.bizState || "", businessZip: form.bizZip || "",
          totalShares: form.totalShares || "", totalParValue: form.parValue || "",
          profession: form.profession || "", licenseNumber: form.licenseNumber || "", licenseHolder: form.licenseHolder || "",
          nonprofitDescription: form.nonprofitDescription || "",
          isManagerManaged,
        },
        contact: {
          firstName: form.contactFirstName, lastName: form.contactLastName,
          email: form.contactEmail, phone: form.contactPhone || "",
          address: form.contactAddress || "", city: form.contactCity || "",
          state: form.contactState || "", zip: form.contactZip || "",
        },
        registeredAgent: {
          useCorpnet: useCorpnetRA,
          name: form.raName || "", address: form.raAddress || "",
          city: form.raCity || "", state: form.raState || "", zip: form.raZip || "",
        },
        einInfo: {
          responsibleParty: form.einResponsibleParty || "",
          ssn: form.einSSN || "", signerNameTitle: form.signerNameTitle || "",
        },
        members: [
          { name: form.member1Name || "", address: form.member1Address || "", city: form.member1City || "", state: form.member1State || "", zip: form.member1Zip || "", ownership: form.member1Ownership || "", type: form.member1Type || "member" },
          showMember2 ? { name: form.member2Name || "", address: form.member2Address || "", city: form.member2City || "", state: form.member2State || "", zip: form.member2Zip || "", ownership: form.member2Ownership || "", type: form.member2Type || "member" } : null,
        ].filter(Boolean),
        managers: isManagerManaged ? [
          { name: form.manager1Name || "", address: form.manager1Address || "", city: form.manager1City || "", state: form.manager1State || "", zip: form.manager1Zip || "" },
        ].filter(m => m.name) : [],
        shareholders: [
          { name: form.sh1Name || "", address: form.sh1Address || "", city: form.sh1City || "", state: form.sh1State || "", zip: form.sh1Zip || "", ownership: form.sh1Ownership || "", ssn: form.sh1SSN || "" },
          showShareholder2 ? { name: form.sh2Name || "", address: form.sh2Address || "", city: form.sh2City || "", state: form.sh2State || "", zip: form.sh2Zip || "", ownership: form.sh2Ownership || "", ssn: form.sh2SSN || "" } : null,
        ].filter(Boolean),
        directors: [
          { name: form.dir1Name || "", address: form.dir1Address || "", city: form.dir1City || "", state: form.dir1State || "", zip: form.dir1Zip || "" },
          showDirector2 ? { name: form.dir2Name || "", address: form.dir2Address || "", city: form.dir2City || "", state: form.dir2State || "", zip: form.dir2Zip || "" } : null,
        ].filter(Boolean),
        officers: {
          president: form.president || "", vicePresident: form.vicePresident || "",
          secretary: form.secretary || "", treasurer: form.treasurer || "", chairman: form.chairman || "",
        },
      };

      const res = await fetch(`${API}/corpnet/submit-formation`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderBody),
      });

      if (res.ok) {
        setOrderComplete(true);
        toast.success("Business formation order submitted!");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to submit. Please contact us at 215-443-1076.");
      }
    } catch { toast.error("Connection error. Please try again."); }
    setSubmitting(false);
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-white">
        <header className="bg-[#333333] text-white"><div className="max-w-5xl mx-auto px-4 py-4"><Link to="/" className="flex items-center gap-2"><img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" width="auto" height="28" /></Link></div></header>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-[#39FF14]/20 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-10 h-10 text-[#39FF14]" /></div>
          <h1 className="text-3xl font-bold text-neutral-900 mb-3">Order Submitted!</h1>
          <p className="text-neutral-600 mb-2">Your <strong>{entityType}</strong> formation in <strong>{filingState}</strong> has been submitted for processing.</p>
          <p className="text-sm text-neutral-500 mb-8">You'll receive email updates as your filing progresses.</p>
          <div className="flex justify-center gap-3">
            <Link to="/clarity-assessment"><Button className="bg-[#333333] text-white">Take Business Assessment</Button></Link>
            <Button variant="outline" onClick={() => { setOrderComplete(false); setStep(1); setEntityType(""); setFilingState(""); setForm({}); }}>Start Another Filing</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="business-formation-page">
      <header className="bg-[#333333] text-white">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><img src="/mybiz-logo.png" alt="My Biz Consulting" className="h-7 brightness-0 invert" width="auto" height="28" /><span className="text-sm text-neutral-400">Business Formation</span></Link>
          <Link to="/" className="text-neutral-300 hover:text-white text-sm flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Home</Link>
        </div>
      </header>

      <section className="bg-[#333333] text-white pb-10 pt-6">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Badge className="bg-[#39FF14] text-black mb-3">Business Formation Services</Badge>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Start Your Business the Right Way</h1>
          <p className="text-neutral-300 text-sm">Professional filing in all 50 U.S. states</p>
        </div>
      </section>

      {/* Progress Bar */}
      <div className="max-w-3xl mx-auto px-4 py-4">
        <div className="flex items-center gap-1 mb-1">
          {[1,2,3,4].map(s => (
            <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? "bg-[#39FF14]" : "bg-neutral-200"}`} />
          ))}
        </div>
        <p className="text-xs text-neutral-500">Step {step} of {totalSteps}: {["Entity & State","Company Details","People & Structure","Review & Submit"][step-1]}</p>
      </div>

      <main className="max-w-3xl mx-auto px-4 pb-12">
        {/* STEP 1: Entity Type & State */}
        {step === 1 && (
          <div className="space-y-6">
            {/* Order Mode Toggle */}
            <div className="flex gap-2 p-1 bg-neutral-100 rounded-lg w-fit">
              <button onClick={() => setOrderMode("formation")} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${orderMode === "formation" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500"}`}>
                New Business Formation
              </button>
              <button onClick={() => setOrderMode("standalone")} className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${orderMode === "standalone" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-500"}`}>
                Standalone Services
              </button>
            </div>

            {orderMode === "standalone" ? (
              <>
                <h2 className="text-xl font-bold text-neutral-900">Order Individual Services</h2>
                <p className="text-sm text-neutral-600">Select the services you need — no formation required. Choose your state below.</p>
                <div className="max-w-md">
                  <Label className="text-sm">State *</Label>
                  <Select value={filingState} onValueChange={setFilingState}>
                    <SelectTrigger><SelectValue placeholder="Select state..." /></SelectTrigger>
                    <SelectContent className="max-h-60">{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold text-neutral-900">Choose Entity Type & State</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {ENTITY_TYPES.map(et => (
                <Card key={et.value} className={`cursor-pointer transition-all hover:shadow-md ${entityType === et.value ? "ring-2 ring-[#39FF14] border-[#39FF14]" : "border-neutral-200"}`}
                  onClick={() => setEntityType(et.value)} data-testid={`entity-${et.value}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <et.icon className={`w-5 h-5 ${entityType === et.value ? "text-[#2BC40E]" : "text-neutral-400"}`} />
                      {et.popular && <Badge className="bg-[#39FF14] text-black text-[9px]">Most Popular</Badge>}
                    </div>
                    <h3 className="font-semibold text-neutral-900 text-sm mb-0.5">{et.label}</h3>
                    <p className="text-[11px] text-neutral-500">{et.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Filing State *</Label>
                <Select value={filingState} onValueChange={setFilingState}>
                  <SelectTrigger><SelectValue placeholder="Select state..." /></SelectTrigger>
                  <SelectContent className="max-h-60">{US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Filing Method</Label>
                <Select value={filingMethod} onValueChange={setFilingMethod}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{FILING_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {sCorpElection && entityType === "LLC" && (
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={sCorpElection} onCheckedChange={setSCorpElection} />
                <span className="text-sm text-neutral-700">Elect S-Corporation tax status</span>
              </label>
            )}

            {/* Formation Package Pricing — selectable */}
            {entityType && orderMode === "formation" && (
              <div className="pt-4 border-t">
                <h3 className="text-lg font-bold text-neutral-900 mb-3">Formation Packages</h3>
                <div className="grid md:grid-cols-3 gap-3">
                  {[
                    { name: "Basic", price: "$111", features: ["Name availability check", "Preparation of Articles", "Free compliance tool with alerts", "Free registered agent (60 days)"] },
                    { name: "Deluxe", price: "$231", popular: true, features: ["Everything in Basic", "Federal Tax ID Number (EIN)", "Free registered agent (1st year)"] },
                    { name: "Complete", price: "$281", features: ["Everything in Deluxe", "Certified filed copy", "Custom Bylaws/Operating Agreement", "Custom Kit and Seal", "Online document access", "Lifetime support"] },
                  ].map(pkg => (
                    <Card key={pkg.name} className={`cursor-pointer transition-all hover:shadow-md ${selectedPackage === pkg.name ? "ring-2 ring-[#39FF14] border-[#39FF14]" : pkg.popular && !selectedPackage ? "ring-2 ring-[#39FF14] border-[#39FF14]" : "border-neutral-200"}`}
                      onClick={() => setSelectedPackage(pkg.name)}>
                      <CardContent className="p-4 text-center">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          {selectedPackage === pkg.name && <CheckCircle className="w-4 h-4 text-[#39FF14]" />}
                          {pkg.popular && <Badge className="bg-[#39FF14] text-black text-[9px]">Best Value</Badge>}
                        </div>
                        <h4 className="font-bold text-neutral-900">{pkg.name}</h4>
                        <p className="text-2xl font-bold text-[#2BC40E] my-2">{pkg.price}</p>
                        <p className="text-[10px] text-neutral-500 mb-3">+ state filing fees</p>
                        <ul className="text-xs text-neutral-600 text-left space-y-1">
                          {pkg.features.map((f, i) => <li key={i} className="flex items-start gap-1"><CheckCircle className="w-3 h-3 text-[#39FF14] mt-0.5 flex-shrink-0" />{f}</li>)}
                        </ul>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <p className="text-[10px] text-neutral-400 mt-2">Express processing: add $150 | 24-Hour rush: add $250. State fees, shipping, and 3% convenience fee not included.</p>
              </div>
            )}

            {/* Additional Services & Pricing — selectable */}
            {(entityType || orderMode === "standalone") && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-neutral-900">Additional Services</h3>
                  {selectedAddons.length > 0 && (
                    <Badge className="bg-[#39FF14] text-black">{selectedAddons.length} selected</Badge>
                  )}
                </div>
                <p className="text-xs text-neutral-500 mb-3">Click to add services to your order — available as add-ons or standalone</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {[
                    { service: "DBA (Doing Business As)", price: 111 },
                    { service: "Federal Tax ID (EIN) — Online", price: 81 },
                    { service: "Federal Tax ID (EIN) — Paper", price: 111 },
                    { service: "S-Corp Election Application", price: 111 },
                    { service: "C-Corp Election Application", price: 111 },
                    { service: "Registered Agent (Annual)", price: 161, suffix: "/yr" },
                    { service: "Annual Report Filing", price: 111 },
                    { service: "Custom LLC Operating Agreement", price: 111 },
                    { service: "Custom Bylaws & Minutes", price: 111 },
                    { service: "Custom Kit with Seal/Embosser", price: 112 },
                    { service: "Seal/Embosser Only", price: 52 },
                    { service: "Stock/Member Certificates (25)", price: 37 },
                    { service: "Articles of Amendment", price: 211 },
                    { service: "Foreign Qualification", price: 251 },
                    { service: "Foreign Qualification + RA", price: 400 },
                    { service: "Certificate of Good Standing", price: 81 },
                    { service: "Certified Copies of Documents", price: 111 },
                    { service: "Business Name Reservation", price: 81 },
                    { service: "Initial Report Filing", price: 111 },
                    { service: "BOI Reporting", price: 211 },
                    { service: "Annual Meeting Minutes", price: 211 },
                    { service: "Change Registered Agent", price: 211 },
                    { service: "Dissolution of Business", price: 311 },
                    { service: "Apostille Certification", price: 211 },
                    { service: "IRS Form Change Letter", price: 81 },
                    { service: "Payroll Tax Account Closure", price: 211 },
                    { service: "Sales Tax Registration", price: 211 },
                    { service: "Corporate Income Tax Reg.", price: 211 },
                    { service: "Business License Research", price: 211 },
                    { service: "Payroll Tax — SIT Registration", price: 211 },
                    { service: "Payroll Tax — SUI Registration", price: 211 },
                    { service: "Trademark Search (Word)", price: 311 },
                    { service: "Trademark Search (Logo)", price: 411 },
                    { service: "Trademark Search (Word + Logo)", price: 511 },
                  ].map(s => {
                    const selected = selectedAddons.includes(s.service);
                    return (
                      <button key={s.service} onClick={() => toggleAddon(s.service)}
                        className={`flex items-center justify-between p-2.5 rounded-lg border text-sm text-left w-full transition-all ${selected ? "bg-[#39FF14]/10 border-[#39FF14]/40 ring-1 ring-[#39FF14]/30" : "bg-neutral-50 border-neutral-100 hover:border-neutral-300"}`}>
                        <span className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${selected ? "bg-[#39FF14] border-[#39FF14]" : "border-neutral-300"}`}>
                            {selected && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                          <span className={selected ? "text-neutral-900 font-medium" : "text-neutral-700"}>{s.service}</span>
                        </span>
                        <span className="font-semibold text-neutral-900 flex-shrink-0 ml-2">${s.price}{s.suffix || ""}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-neutral-400 mt-2">Prices for standard processing. Express and 24-hour rush available. State fees not included.</p>
              </div>
            )}

            <div className="flex justify-end">
              {orderMode === "formation" ? (
                <Button onClick={() => { if (!entityType || !filingState) { toast.error("Select entity type and state"); return; } setStep(2); }}
                  className="bg-[#333333] text-white" data-testid="next-step-1">Next: Company Details <ArrowRight className="w-4 h-4 ml-2" /></Button>
              ) : (
                <Button onClick={() => { if (!filingState) { toast.error("Select a state"); return; } if (selectedAddons.length === 0) { toast.error("Select at least one service"); return; } setStep(4); }}
                  className="bg-[#333333] text-white" data-testid="next-standalone">Continue to Contact Info <ArrowRight className="w-4 h-4 ml-2" /></Button>
              )}
            </div>
            {/* close formation/standalone ternary */}
            {orderMode === "formation" && <></>}
            </>
            )}
          </div>
        )}

        {/* STEP 2: Company Information */}
        {step === 2 && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-neutral-900">Company Information</h2>
            <div className="space-y-4">
              <div><Label className="text-sm">Desired Business Name *</Label><Input value={form.companyName || ""} onChange={e => setForm({...form, companyName: e.target.value})} placeholder="Your Business Name LLC" data-testid="company-name-input" /></div>
              <div><Label className="text-sm">Alternative Name (backup)</Label><Input value={form.altName || ""} onChange={e => setForm({...form, altName: e.target.value})} placeholder="Second choice if first is taken" /></div>
              
              {entityType === "Nonprofit" ? (
                <div><Label className="text-sm">Nonprofit Purpose/Description *</Label><Textarea value={form.nonprofitDescription || ""} onChange={e => setForm({...form, nonprofitDescription: e.target.value})} placeholder="Describe your nonprofit's purpose..." /></div>
              ) : (
                <div><Label className="text-sm">Business Description</Label><Input value={form.businessDescription || ""} onChange={e => setForm({...form, businessDescription: e.target.value})} placeholder="Brief description of business activities" /></div>
              )}

              {entityType === "ProfessionalCorp" && (
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label className="text-sm">Profession *</Label><Input value={form.profession || ""} onChange={e => setForm({...form, profession: e.target.value})} placeholder="e.g., Law, Medicine" /></div>
                  <div><Label className="text-sm">License # *</Label><Input value={form.licenseNumber || ""} onChange={e => setForm({...form, licenseNumber: e.target.value})} placeholder="License number" /></div>
                  <div><Label className="text-sm">License Holder Name *</Label><Input value={form.licenseHolder || ""} onChange={e => setForm({...form, licenseHolder: e.target.value})} placeholder="Exact name on license" /></div>
                </div>
              )}

              {(entityType === "Corporation" || entityType === "ProfessionalCorp") && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-sm">Total Shares</Label><Input value={form.totalShares || ""} onChange={e => setForm({...form, totalShares: e.target.value})} placeholder="e.g., 1000" /></div>
                  <div><Label className="text-sm">Par Value per Share</Label><Input value={form.parValue || ""} onChange={e => setForm({...form, parValue: e.target.value})} placeholder="e.g., $0.01" /></div>
                </div>
              )}

              <div className="pt-2"><Label className="text-sm font-semibold">Business Address</Label></div>
              <div><Input value={form.bizAddress1 || ""} onChange={e => setForm({...form, bizAddress1: e.target.value})} placeholder="Street address line 1" /></div>
              <div><Input value={form.bizAddress2 || ""} onChange={e => setForm({...form, bizAddress2: e.target.value})} placeholder="Street address line 2 (optional)" /></div>
              <div className="grid grid-cols-3 gap-3">
                <Input value={form.bizCity || ""} onChange={e => setForm({...form, bizCity: e.target.value})} placeholder="City" />
                <Input value={form.bizState || ""} onChange={e => setForm({...form, bizState: e.target.value})} placeholder="State" />
                <Input value={form.bizZip || ""} onChange={e => setForm({...form, bizZip: e.target.value})} placeholder="ZIP" />
              </div>

              {/* Registered Agent */}
              <div className="pt-2 border-t"><Label className="text-sm font-semibold">Registered Agent</Label></div>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={useCorpnetRA} onCheckedChange={setUseCorpnetRA} />
                <span className="text-sm text-neutral-700">Use CorpNet Registered Agent (Free 1st year, $149/yr thereafter)</span>
              </label>
              {!useCorpnetRA && (
                <div className="space-y-3 pl-6">
                  <Input value={form.raName || ""} onChange={e => setForm({...form, raName: e.target.value})} placeholder="Registered Agent Name" />
                  <Input value={form.raAddress || ""} onChange={e => setForm({...form, raAddress: e.target.value})} placeholder="Address" />
                  <div className="grid grid-cols-3 gap-3">
                    <Input value={form.raCity || ""} onChange={e => setForm({...form, raCity: e.target.value})} placeholder="City" />
                    <Input value={form.raState || ""} onChange={e => setForm({...form, raState: e.target.value})} placeholder="State" />
                    <Input value={form.raZip || ""} onChange={e => setForm({...form, raZip: e.target.value})} placeholder="ZIP" />
                  </div>
                </div>
              )}

              {/* EIN */}
              <div className="pt-2 border-t"><Label className="text-sm font-semibold">Tax ID Number (EIN) Information</Label></div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Name of Responsible Party</Label><Input value={form.einResponsibleParty || ""} onChange={e => setForm({...form, einResponsibleParty: e.target.value})} placeholder="Full legal name" /></div>
                <div><Label className="text-xs">Social Security #</Label><Input type="password" value={form.einSSN || ""} onChange={e => setForm({...form, einSSN: e.target.value})} placeholder="XXX-XX-XXXX" /></div>
              </div>
              {entityType === "Nonprofit" && (
                <div><Label className="text-xs">Signer on Behalf (Name & Title)</Label><Input value={form.signerNameTitle || ""} onChange={e => setForm({...form, signerNameTitle: e.target.value})} placeholder="e.g., Jane Doe, President" /></div>
              )}
            </div>
            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
              <Button onClick={() => { if (!form.companyName) { toast.error("Company name is required"); return; } setStep(3); }}
                className="bg-[#333333] text-white">Next: People & Structure <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </div>
          </div>
        )}

        {/* STEP 3: People & Structure */}
        {step === 3 && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-neutral-900">People & Structure</h2>

            {/* LLC: Members & Managers */}
            {entityType === "LLC" && (
              <>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={isManagerManaged} onCheckedChange={setIsManagerManaged} />
                  <span className="text-sm text-neutral-700">This LLC is Manager-Managed</span>
                </label>
                <PersonFields prefix="member1" label="Member 1 (Owner)" form={form} setForm={setForm} showOwnership />
                {showMember2 ? <PersonFields prefix="member2" label="Member 2" form={form} setForm={setForm} showOwnership /> :
                  <Button variant="ghost" className="text-[#2BC40E]" onClick={() => setShowMember2(true)}>+ Add Member 2</Button>}
                {isManagerManaged && <PersonFields prefix="manager1" label="Manager 1" form={form} setForm={setForm} />}
                <div className="pt-2 border-t"><Label className="text-sm font-semibold">Officers (Optional)</Label></div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label className="text-xs">President</Label><Input value={form.president || ""} onChange={e => setForm({...form, president: e.target.value})} /></div>
                  <div><Label className="text-xs">Secretary</Label><Input value={form.secretary || ""} onChange={e => setForm({...form, secretary: e.target.value})} /></div>
                  <div><Label className="text-xs">Chairman</Label><Input value={form.chairman || ""} onChange={e => setForm({...form, chairman: e.target.value})} /></div>
                </div>
              </>
            )}

            {/* Corporation & Professional Corp: Shareholders, Directors, Officers */}
            {(entityType === "Corporation" || entityType === "ProfessionalCorp") && (
              <>
                <PersonFields prefix="sh1" label="Shareholder (Owner) 1" form={form} setForm={setForm} showOwnership showSSN={sCorpElection} />
                {showShareholder2 ? <PersonFields prefix="sh2" label="Shareholder (Owner) 2" form={form} setForm={setForm} showOwnership showSSN={sCorpElection} /> :
                  <Button variant="ghost" className="text-[#2BC40E]" onClick={() => setShowShareholder2(true)}>+ Add Shareholder 2</Button>}
                <PersonFields prefix="dir1" label="Director 1" form={form} setForm={setForm} />
                {showDirector2 ? <PersonFields prefix="dir2" label="Director 2" form={form} setForm={setForm} /> :
                  <Button variant="ghost" className="text-[#2BC40E]" onClick={() => setShowDirector2(true)}>+ Add Director 2</Button>}
                <div className="pt-2 border-t"><Label className="text-sm font-semibold">Officers</Label></div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">President</Label><Input value={form.president || ""} onChange={e => setForm({...form, president: e.target.value})} /></div>
                  <div><Label className="text-xs">Vice President</Label><Input value={form.vicePresident || ""} onChange={e => setForm({...form, vicePresident: e.target.value})} /></div>
                  <div><Label className="text-xs">Secretary</Label><Input value={form.secretary || ""} onChange={e => setForm({...form, secretary: e.target.value})} /></div>
                  <div><Label className="text-xs">Treasurer</Label><Input value={form.treasurer || ""} onChange={e => setForm({...form, treasurer: e.target.value})} /></div>
                </div>
              </>
            )}

            {/* Nonprofit: Directors & Officers */}
            {entityType === "Nonprofit" && (
              <>
                <PersonFields prefix="dir1" label="Director 1" form={form} setForm={setForm} />
                {showDirector2 ? <PersonFields prefix="dir2" label="Director 2" form={form} setForm={setForm} /> :
                  <Button variant="ghost" className="text-[#2BC40E]" onClick={() => setShowDirector2(true)}>+ Add Director 2</Button>}
                <div className="pt-2 border-t"><Label className="text-sm font-semibold">Officers</Label></div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label className="text-xs">President</Label><Input value={form.president || ""} onChange={e => setForm({...form, president: e.target.value})} /></div>
                  <div><Label className="text-xs">Vice President</Label><Input value={form.vicePresident || ""} onChange={e => setForm({...form, vicePresident: e.target.value})} /></div>
                  <div><Label className="text-xs">Secretary</Label><Input value={form.secretary || ""} onChange={e => setForm({...form, secretary: e.target.value})} /></div>
                  <div><Label className="text-xs">Treasurer</Label><Input value={form.treasurer || ""} onChange={e => setForm({...form, treasurer: e.target.value})} /></div>
                </div>
              </>
            )}

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
              <Button onClick={() => setStep(4)} className="bg-[#333333] text-white">Next: Review & Submit <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </div>
          </div>
        )}

        {/* STEP 4: Contact & Review */}
        {step === 4 && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-neutral-900">Contact Information & Review</h2>

            <Card className="border-[#39FF14]/20 bg-[#39FF14]/5">
              <CardContent className="p-4">
                <h3 className="font-semibold text-sm text-neutral-900 mb-2">Order Summary</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-neutral-500">Order Type:</p><p className="font-medium capitalize">{orderMode === "formation" ? "Business Formation" : "Standalone Services"}</p>
                  {orderMode === "formation" && <><p className="text-neutral-500">Entity Type:</p><p className="font-medium">{entityType}</p></>}
                  <p className="text-neutral-500">State:</p><p className="font-medium">{filingState}</p>
                  {form.companyName && <><p className="text-neutral-500">Business Name:</p><p className="font-medium">{form.companyName}</p></>}
                  {selectedPackage && <><p className="text-neutral-500">Package:</p><p className="font-medium text-[#2BC40E]">{selectedPackage}</p></>}
                  {sCorpElection && <><p className="text-neutral-500">S-Corp Election:</p><p className="font-medium text-[#2BC40E]">Yes</p></>}
                </div>
                {selectedAddons.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#39FF14]/20">
                    <p className="text-xs font-semibold text-neutral-700 mb-1">Additional Services ({selectedAddons.length}):</p>
                    <div className="flex flex-wrap gap-1">
                      {selectedAddons.map(a => <Badge key={a} variant="outline" className="text-[10px] border-[#39FF14]/30 text-[#2BC40E]">{a}</Badge>)}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="space-y-4">
              <Label className="text-sm font-semibold">Your Contact Information</Label>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">First Name *</Label><Input value={form.contactFirstName || ""} onChange={e => setForm({...form, contactFirstName: e.target.value})} placeholder="First name" data-testid="contact-first" /></div>
                <div><Label className="text-xs">Last Name *</Label><Input value={form.contactLastName || ""} onChange={e => setForm({...form, contactLastName: e.target.value})} placeholder="Last name" data-testid="contact-last" /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Email *</Label><Input type="email" value={form.contactEmail || ""} onChange={e => setForm({...form, contactEmail: e.target.value})} placeholder="email@example.com" data-testid="contact-email" /></div>
                <div><Label className="text-xs">Phone</Label><Input value={form.contactPhone || ""} onChange={e => setForm({...form, contactPhone: e.target.value})} placeholder="215-555-1234" /></div>
              </div>
              <div><Input value={form.contactAddress || ""} onChange={e => setForm({...form, contactAddress: e.target.value})} placeholder="Street address" /></div>
              <div className="grid grid-cols-3 gap-3">
                <Input value={form.contactCity || ""} onChange={e => setForm({...form, contactCity: e.target.value})} placeholder="City" />
                <Input value={form.contactState || ""} onChange={e => setForm({...form, contactState: e.target.value})} placeholder="State" />
                <Input value={form.contactZip || ""} onChange={e => setForm({...form, contactZip: e.target.value})} placeholder="ZIP" />
              </div>
            </div>

            <div className="bg-neutral-50 rounded-lg p-4 border border-neutral-200 text-xs space-y-1">
              <p className="font-semibold text-neutral-900 text-sm mb-2">What happens next?</p>
              <p className="flex items-start gap-1"><CheckCircle className="w-3 h-3 text-[#39FF14] mt-0.5" /> We verify your business name availability</p>
              <p className="flex items-start gap-1"><CheckCircle className="w-3 h-3 text-[#39FF14] mt-0.5" /> Formation documents are prepared and filed with the state</p>
              <p className="flex items-start gap-1"><CheckCircle className="w-3 h-3 text-[#39FF14] mt-0.5" /> You receive confirmation and all filed documents</p>
              <p className="flex items-start gap-1"><CheckCircle className="w-3 h-3 text-[#39FF14] mt-0.5" /> A My Biz Consulting strategist follows up to support your growth</p>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}><ArrowLeft className="w-4 h-4 mr-2" /> Back</Button>
              <Button onClick={handleSubmitOrder} disabled={submitting} className="bg-[#39FF14] hover:bg-[#32E010] text-black h-12 px-8 font-semibold" data-testid="submit-order-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <FileText className="w-5 h-5 mr-2" />}
                {submitting ? "Submitting..." : "Submit Formation Order"}
              </Button>
            </div>
          </div>
        )}

        {/* Help */}
        <Card className="border-neutral-200 bg-neutral-50 mt-8">
          <CardContent className="p-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-neutral-900 text-sm">Need help?</p>
              <p className="text-xs text-neutral-600">Call us or take a free assessment to find the right entity type.</p>
            </div>
            <div className="flex gap-2">
              <a href="tel:215-443-1076"><Button variant="outline" size="sm"><Phone className="w-3.5 h-3.5 mr-1" /> 215-443-1076</Button></a>
              <Link to="/clarity-assessment"><Button variant="outline" size="sm">Free Assessment</Button></Link>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BusinessFormation;
